/**
 * Settings → Subscription (D-147): the agency's plan and its status, the usage against the
 * quotas, the invoices of the platform operator and their payment (CinetPay, Moneroo as
 * fallback; in test mode the local simulator answers, no money). Paying works even when the
 * workspace is read-only: it reactivates the workspace at once.
 */

import {
  type SaasInvoice,
  type SaasPayment,
  useSaasInvoicesDocumentRetrieve,
  useSaasInvoicesList,
  useSaasInvoicesPayCreate,
  useSaasPaymentsRetrieve,
  useSaasPaymentsSimulateCreate,
  useSaasPlansList,
  useSaasRemindersList,
  useSaasUsageRetrieve,
} from "@agencyos/api-client";
import { Badge, type BadgeTone, Button, Dialog, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { SettingsLayout } from "../../components/SettingsLayout";
import { dateText } from "../billing/shared";
import { formatAmount, useSaasStatus } from "./useSaas";
import "./saas.css";

const STATUS_TONE: Record<string, BadgeTone> = {
  trial: "info",
  active: "success",
  past_due: "warning",
  read_only: "danger",
  cancelled: "danger",
};

const INVOICE_TONE: Record<string, BadgeTone> = { open: "warning", paid: "success", void: "default" };

type Texts = Record<string, string>;

function textOf(value: unknown, language: string): string {
  const texts = (value ?? {}) as Texts;
  return texts[language] ?? texts.fr ?? "";
}

function useRefresh() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").includes("/saas/") });
}

function PlanCard() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "fr";
  const status = useSaasStatus();
  const plans = useSaasPlansList();
  if (!status) return <Skeleton style={{ height: 140 }} />;
  if (!status.subscribed) {
    return (
      <GlassCard as="section" className="stack">
        <p>{t("saas.noSubscription")}</p>
      </GlassCard>
    );
  }
  const plan = (status.plan ?? {}) as { code?: string; name?: unknown; price_xaf?: number };
  const detail = plans.data?.status === 200 ? plans.data.data.find((p) => p.code === plan.code) : undefined;
  return (
    <GlassCard as="section" className="stack" aria-labelledby="saas-plan-title">
      <div className="saas-row">
        <h2 id="saas-plan-title">
          {t("saas.plan.title")} : {textOf(plan.name, language)}
        </h2>
        <Badge tone={STATUS_TONE[status.status] ?? "default"}>{t(`saas.status.${status.status}`)}</Badge>
      </div>
      {plan.price_xaf !== undefined ? <p>{t("saas.plan.price", { amount: formatAmount(plan.price_xaf) })}</p> : null}
      {status.status === "trial" && status.trial_ends_at ? (
        <p className="muted">{t("saas.plan.trialEnds", { date: dateText(status.trial_ends_at, language) })}</p>
      ) : status.current_period_end ? (
        <p className="muted">{t("saas.plan.nextDue", { date: dateText(status.current_period_end, language) })}</p>
      ) : null}
      {status.status === "past_due" && status.grace_ends_at ? (
        <p role="alert">{t("saas.plan.graceEnds", { date: dateText(status.grace_ends_at, language) })}</p>
      ) : null}
      {status.billing_enabled ? null : <p className="muted">{t("saas.plan.billingOff")}</p>}
      {detail ? (
        <div>
          <h3>{t("saas.plan.modules")}</h3>
          <p className="saas-modules">{status.modules.join(" · ")}</p>
        </div>
      ) : null}
    </GlassCard>
  );
}

function UsageCard() {
  const { t } = useTranslation();
  const query = useSaasUsageRetrieve();
  const usage = query.data?.status === 200 ? query.data.data : undefined;
  if (!usage) return query.isPending ? <Skeleton style={{ height: 120 }} /> : null;
  return (
    <GlassCard as="section" className="stack" aria-labelledby="saas-usage-title">
      <h2 id="saas-usage-title">{t("saas.usage.title")}</h2>
      <p className="muted">{t("saas.usage.intro")}</p>
      <ul className="saas-usage">
        {usage.quotas.map((quota) => (
          <li key={quota.metric} className={quota.over ? "saas-usage__over" : undefined}>
            <span>{t(`saas.usage.metrics.${quota.metric}`)}</span>
            <strong>
              {quota.limit === null ? `${quota.used} (${t("saas.usage.unlimited")})` : t("saas.usage.of", { used: quota.used, limit: quota.limit })}
            </strong>
            {quota.limit !== null ? (
              <progress max={100} value={Math.min(quota.percent ?? 0, 100)} aria-label={t(`saas.usage.metrics.${quota.metric}`)} />
            ) : null}
            {quota.over ? <Badge tone="danger">{t("saas.usage.over")}</Badge> : null}
          </li>
        ))}
      </ul>
    </GlassCard>
  );
}

function InvoiceDocument({ invoice, onClose }: { invoice: SaasInvoice; onClose: () => void }) {
  const { t } = useTranslation();
  const frame = useRef<HTMLIFrameElement>(null);
  const query = useSaasInvoicesDocumentRetrieve(invoice.id);
  const html = query.data?.status === 200 ? query.data.data.html : "";
  return (
    <Dialog
      open
      title={invoice.number}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={() => frame.current?.contentWindow?.print()} disabled={!html}>
            {t("saas.invoices.print")}
          </Button>
          <Button onClick={onClose}>{t("saas.invoices.close")}</Button>
        </>
      }
    >
      {html ? (
        <iframe ref={frame} title={invoice.number} srcDoc={html} sandbox="allow-modals allow-same-origin" className="saas-document" />
      ) : (
        <Skeleton style={{ height: 300 }} />
      )}
    </Dialog>
  );
}

function PaymentDialog({ invoice, payment, onClose }: { invoice: SaasInvoice; payment: string; onClose: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefresh();
  const query = useSaasPaymentsRetrieve(payment, {
    query: {
      refetchInterval: (q) => {
        const data = q.state.data;
        return data?.status === 200 && data.data.status === "pending" && !data.data.simulated ? 5_000 : false;
      },
    },
  });
  const simulate = useSaasPaymentsSimulateCreate({ mutation: { onSuccess: () => { void query.refetch(); refresh(); } } });
  const state: SaasPayment | undefined = query.data?.status === 200 ? query.data.data : undefined;
  return (
    <Dialog open title={t("saas.payment.title", { number: invoice.number })} onClose={onClose} actions={<Button onClick={onClose}>{t("saas.invoices.close")}</Button>}>
      {!state ? (
        <Skeleton style={{ height: 60 }} />
      ) : state.status === "confirmed" ? (
        <p role="status">{t("saas.payment.confirmed")}</p>
      ) : state.status === "failed" ? (
        <p role="alert">{t("saas.payment.failed")}</p>
      ) : state.simulated ? (
        <div className="stack">
          <p>{t("saas.payment.simulator")}</p>
          <div className="saas-row">
            <Button onClick={() => simulate.mutate({ id: payment, data: { success: true } })} disabled={simulate.isPending}>
              {t("saas.payment.simulateSuccess")}
            </Button>
            <Button variant="secondary" onClick={() => simulate.mutate({ id: payment, data: { success: false } })} disabled={simulate.isPending}>
              {t("saas.payment.simulateFailure")}
            </Button>
          </div>
        </div>
      ) : (
        <p role="status">{t("saas.payment.pending")}</p>
      )}
    </Dialog>
  );
}

function InvoicesCard() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage === "en" ? "en" : "fr";
  const [params, setParams] = useSearchParams();
  const list = useSaasInvoicesList();
  const [viewing, setViewing] = useState<SaasInvoice | null>(null);
  const [paying, setPaying] = useState<{ invoice: SaasInvoice; payment: string } | null>(null);
  const [error, setError] = useState("");
  const pay = useSaasInvoicesPayCreate();
  const invoices = list.data?.status === 200 ? list.data.data.results : [];
  const returned = params.get("payment");
  const returnedInvoice = returned ? invoices.find((i) => i.status === "open") ?? invoices[0] : undefined;

  const start = (invoice: SaasInvoice) => {
    setError("");
    pay.mutate(
      { id: invoice.id, data: { language } },
      {
        onSuccess: (response) => {
          if (response.status !== 201) {
            setError(t("saas.payment.unavailable"));
            return;
          }
          if (response.data.simulated || !response.data.redirect_url) setPaying({ invoice, payment: response.data.payment });
          else window.location.assign(response.data.redirect_url);
        },
        onError: () => setError(t("saas.payment.unavailable")),
      },
    );
  };

  return (
    <GlassCard as="section" className="stack" aria-labelledby="saas-invoices-title">
      <h2 id="saas-invoices-title">{t("saas.invoices.title")}</h2>
      {error ? <p role="alert">{error}</p> : null}
      {list.isPending ? (
        <Skeleton style={{ height: 80 }} />
      ) : invoices.length === 0 ? (
        <p className="muted">{t("saas.invoices.empty")}</p>
      ) : (
        <div className="table-scroll">
          <table className="ao-table">
            <thead>
              <tr>
                <th>{t("saas.invoices.number")}</th>
                <th>{t("saas.invoices.period")}</th>
                <th>{t("saas.invoices.due")}</th>
                <th>{t("saas.invoices.amount")}</th>
                <th>{t("saas.invoices.statusLabel")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>{invoice.number}</td>
                  <td>
                    {dateText(invoice.period_start, language)} → {dateText(invoice.period_end, language)}
                  </td>
                  <td>{dateText(invoice.due_at, language)}</td>
                  <td className="num">{formatAmount(invoice.amount_ttc_xaf)}</td>
                  <td>
                    <Badge tone={INVOICE_TONE[invoice.status] ?? "default"}>{t(`saas.invoices.status.${invoice.status}`)}</Badge>
                  </td>
                  <td className="saas-row">
                    <Button size="sm" variant="plain" onClick={() => setViewing(invoice)}>
                      {t("saas.invoices.view")}
                    </Button>
                    {invoice.status === "open" ? (
                      <Button size="sm" onClick={() => start(invoice)} disabled={pay.isPending}>
                        {t("saas.invoices.pay")}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {viewing ? <InvoiceDocument invoice={viewing} onClose={() => setViewing(null)} /> : null}
      {paying ? <PaymentDialog invoice={paying.invoice} payment={paying.payment} onClose={() => setPaying(null)} /> : null}
      {!paying && returned && returnedInvoice ? (
        <PaymentDialog
          invoice={returnedInvoice}
          payment={returned}
          onClose={() => {
            params.delete("payment");
            setParams(params, { replace: true });
          }}
        />
      ) : null}
    </GlassCard>
  );
}

function RemindersCard() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "fr";
  const list = useSaasRemindersList();
  const rows = list.data?.status === 200 ? list.data.data.results : [];
  if (rows.length === 0) return null;
  return (
    <GlassCard as="section" className="stack" aria-labelledby="saas-reminders-title">
      <h2 id="saas-reminders-title">{t("saas.reminders.title")}</h2>
      <ul className="saas-reminders">
        {rows.map((row) => (
          <li key={row.id}>
            <span>{dateText(row.created_at, language)}</span> · {t(`saas.reminders.kind.${row.kind}`)} · {row.invoice_number} ({formatAmount(row.amount_ttc_xaf)})
          </li>
        ))}
      </ul>
    </GlassCard>
  );
}

export function SubscriptionPage() {
  const { t } = useTranslation();
  return (
    <SettingsLayout title={t("saas.title")} intro={t("saas.intro")}>
      <div className="stack">
        <PlanCard />
        <UsageCard />
        <InvoicesCard />
        <RemindersCard />
      </div>
    </SettingsLayout>
  );
}
